import React, { useEffect } from "react";
import {
  TextField,
  Button,
  Stack,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  InputAdornment,
  Divider,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import { getCountryDataList, getEmojiFlag } from "countries-list";
import { openLogin } from "../../api/auth";
import { User } from "../../store/useAuthStore";

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
}

interface FormContactProps {
  user?: User | null;
  contactInfo: ContactInfo;
  onChange: (field: keyof ContactInfo, value: string) => void;
  isEmailVerified?: boolean;
}

interface CountryOption {
  code: string;
  country: string;
  flag: string;
  name: string;
}

const countries: CountryOption[] = getCountryDataList()
  .filter((country) => country.phone.length > 0)
  .map((country) => ({
    code: `+${country.phone[0]}`,
    country: country.iso2,
    flag: getEmojiFlag(country.iso2),
    name: country.name,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const FormContact: React.FC<FormContactProps> = ({
  user,
  contactInfo,
  onChange,
  isEmailVerified = false,
}) => {
  useEffect(() => {
    if (!contactInfo.countryCode) {
      const laos = countries.find((c) => c.country === "LA");
      if (laos) {
        onChange("countryCode", laos.code);
      }
    }
  }, []);

  const handleLogin = () => {
    openLogin();
  };

  const handleFieldChange =
    (field: keyof ContactInfo) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(field, event.target.value);
    };

  const handleCountryCodeChange = (event: any) => {
    onChange("countryCode", event.target.value);
  };

  // If no user, show only login button
  if (!user) {
    return (
      <Stack spacing={2} alignItems="center">
        <Alert severity="info" sx={{ width: "100%" }}>
          Please log in to continue with your booking
        </Alert>
        <Button
          fullWidth
          variant="contained"
          onClick={handleLogin}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1rem",
          }}
        >
          Login with Google
        </Button>
      </Stack>
    );
  }

  // Get selected country info
  const selectedCountry =
    countries.find((c) => c.code === contactInfo.countryCode) || countries[0];

  // If user is logged in, show contact form
  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={contactInfo.firstName}
            onChange={handleFieldChange("firstName")}
            required
            placeholder="Enter your first name"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={contactInfo.lastName}
            onChange={handleFieldChange("lastName")}
            required
            placeholder="Enter your last name"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={contactInfo.email}
            disabled={true} // Always disabled since it's from authenticated user
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <VerifiedIcon
                    sx={{
                      color: "success.main",
                      fontSize: "1.2rem",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            helperText="Email verified from your account ✓"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "action.hover",
              },
              "& .MuiFormHelperText-root": {
                color: "success.main",
                fontWeight: 500,
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone Number"
            type="tel"
            value={contactInfo.phoneNumber}
            onChange={handleFieldChange("phoneNumber")}
            required
            placeholder="Enter your phone number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FormControl variant="standard" sx={{ minWidth: 80, mr: 1 }}>
                    <Select
                      value={contactInfo.countryCode || "+856"} // Default to Laos
                      onChange={handleCountryCodeChange}
                      disableUnderline
                      sx={{
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          paddingRight: "20px !important",
                        },
                      }}
                      renderValue={(value) => (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <span style={{ fontSize: "1.1em" }}>
                            {selectedCountry?.flag}
                          </span>
                          <Typography variant="body2" color="text.secondary">
                            {value}
                          </Typography>
                        </Box>
                      )}
                    >
                      {countries.map((country) => (
                        <MenuItem
                          key={`${country.code}-${country.country}`}
                          value={country.code}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            <span style={{ fontSize: "1.2em" }}>
                              {country.flag}
                            </span>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                              {country.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {country.code}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 0.5, height: 24 }}
                  />
                </InputAdornment>
              ),
            }}
            helperText="We'll use this to contact you about your booking"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

export default FormContact;
